/**
 * Tasks from text: Claude proposes the action items in this page; the user
 * edits and picks them, and only the checked ones are added (at the end of the
 * page, or to the Inbox).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor, JSONContent } from '@tiptap/core';
import { Loader2 } from 'lucide-react';
import {
  AiError,
  extractMessage,
  runExtract,
  taskText,
  type AiErrorCode,
  type ProposedTask,
} from '@nexus/ai';
import { toIsoDate, type TaskPriority } from '@nexus/core';
import { serializeMarkdown } from '@nexus/markdown';
import { addTaskToPage, inboxPage } from '@nexus/workspace';
import { useStore } from '../db/context';
import { Dialog } from '../components/Dialog';
import { transport, useAiChat } from './chat';

type Row = ProposedTask & { checked: boolean };
type State =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: Row[] }
  | { kind: 'error'; code: AiErrorCode }
  | { kind: 'saving'; rows: Row[] };

const PRIORITIES: TaskPriority[] = ['highest', 'high', 'medium', 'low'];
const field = 'rounded-md border border-border bg-bg px-1.5 py-1 text-sm';

export function ExtractTasksDialog({
  open,
  onOpenChange,
  pageId,
  title,
  editor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageId: string;
  title: string;
  editor: Editor | null;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('ai.extract.title')} wide>
      {open && (
        <ExtractBody
          pageId={pageId}
          title={title}
          editor={editor}
          onDone={() => onOpenChange(false)}
        />
      )}
    </Dialog>
  );
}

function ExtractBody({
  pageId,
  title,
  editor,
  onDone,
}: {
  pageId: string;
  title: string;
  editor: Editor | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [target, setTarget] = useState<'page' | 'inbox'>('page');

  useEffect(() => {
    const controller = new AbortController();
    const now = new Date();
    const markdown = serializeMarkdown(
      (editor?.getJSON() ?? { type: 'doc', content: [] }) as JSONContent,
    );
    runExtract({
      transport,
      model: 'sonnet',
      message: extractMessage({
        title,
        markdown,
        today: toIsoDate(now),
        weekday: new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(now),
      }),
      signal: controller.signal,
      onUsage: (usage) => useAiChat.setState({ usage }),
    })
      .then((tasks) =>
        setState({ kind: 'ready', rows: tasks.map((x) => ({ ...x, checked: true })) }),
      )
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        if (!(e instanceof AiError)) console.error('ai extract', e);
        setState({ kind: 'error', code: e instanceof AiError ? e.code : 'api' });
      });
    return () => controller.abort();
  }, [editor, title]);

  if (state.kind === 'loading')
    return (
      <p className="flex items-center gap-2 text-sm text-fg-muted" aria-live="polite">
        <Loader2 className="size-4 animate-spin" />
        {t('ai.extract.loading')}
      </p>
    );
  if (state.kind === 'error')
    return (
      <p role="alert" className="rounded-md bg-red-500/10 px-2 py-1.5 text-sm text-danger">
        {t(`ai.errors.${state.code}`)}
      </p>
    );

  const rows = state.rows;
  const update = (i: number, patch: Partial<Row>) =>
    setState({ kind: 'ready', rows: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const chosen = rows.filter((r) => r.checked && r.title.trim());

  const add = async () => {
    setState({ kind: 'saving', rows });
    const dest = target === 'page' ? pageId : (await inboxPage(store)).id;
    for (const r of chosen) await addTaskToPage(store, dest, taskText(r));
    onDone();
  };

  if (rows.length === 0) return <p className="text-sm text-fg-muted">{t('ai.extract.none')}</p>;

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-fg-muted">{t('ai.extract.intro')}</p>
      <ul className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <li
            key={i}
            className="flex flex-col gap-1 rounded-lg border border-border p-2"
            data-testid="ai-task"
          >
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="checkbox"
                checked={r.checked}
                onChange={(e) => update(i, { checked: e.target.checked })}
                aria-label={t('ai.extract.include', { title: r.title })}
              />
              <input
                value={r.title}
                onChange={(e) => update(i, { title: e.target.value })}
                aria-label={t('ai.extract.taskTitle')}
                className={`${field} min-w-48 flex-1`}
              />
              <input
                type="date"
                value={r.due ?? ''}
                onChange={(e) =>
                  update(i, { due: e.target.value || null, time: e.target.value ? r.time : null })
                }
                aria-label={t('ai.extract.due')}
                className={field}
              />
              <input
                type="time"
                value={r.time ?? ''}
                disabled={!r.due}
                onChange={(e) => update(i, { time: e.target.value || null })}
                aria-label={t('ai.extract.time')}
                className={field}
              />
              <select
                value={r.priority ?? ''}
                onChange={(e) =>
                  update(i, { priority: (e.target.value || null) as TaskPriority | null })
                }
                aria-label={t('ai.extract.priority')}
                className={field}
              >
                <option value="">{t('ai.extract.noPriority')}</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`ai.extract.priorities.${p}`)}
                  </option>
                ))}
              </select>
            </div>
            {r.quote && <p className="pl-6 text-xs text-fg-muted italic">“{r.quote}”</p>}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <label className="flex items-center gap-1.5">
          {t('ai.extract.target')}
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as 'page' | 'inbox')}
            className={field}
          >
            <option value="page">{t('ai.extract.toPage')}</option>
            <option value="inbox">{t('ai.extract.toInbox')}</option>
          </select>
        </label>
        <button
          type="button"
          disabled={chosen.length === 0 || state.kind === 'saving'}
          onClick={() => void add()}
          className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg disabled:opacity-40"
        >
          {t('ai.extract.add', { count: chosen.length })}
        </button>
      </div>
    </div>
  );
}
